// Manœuvres du tracé (tourner à gauche, rond-point…) et textes en français.
// Logique pure, testée dans __tests__/maneuvers.test.ts.
//
// Deux sources :
//  1. la cartographie (OSRM « map matching », voir osrmMatch.ts) : vraies intersections,
//     ronds-points avec numéro de sortie, noms de rue ;
//  2. en secours, la forme du tracé (detectTurns) : virages nets, sans ronds-points.

import { bearing, cumulativeDistances, LatLng, pointAtAlong, snapToRoute, turnAngle } from './geo';
import { getLang, t } from './i18n';

export type Side = 'left' | 'right' | 'straight';
export type ManeuverKind = 'turn' | 'slight' | 'sharp' | 'uturn' | 'roundabout' | 'keep' | 'arrive';

export type Maneuver = {
  /** Position le long du tracé (m depuis le départ) */
  along: number;
  kind: ManeuverKind;
  side: Side;
  /** Numéro de sortie (rond-point) */
  exit?: number;
  /** Rue empruntée après la manœuvre */
  road?: string;
  location: LatLng;
};

// ---------------------------------------------------------------- textes

function ordinal(n: number): string {
  if (getLang() === 'fr') return n === 1 ? '1re' : `${n}e`;
  const suffix = n % 100 >= 11 && n % 100 <= 13 ? 'th' : (['th', 'st', 'nd', 'rd'][n % 10] ?? 'th');
  return `${n}${suffix}`;
}
const sideWord = (s: Side) =>
  s === 'left' ? t('à gauche', 'left') : s === 'right' ? t('à droite', 'right') : t('tout droit', 'straight');

/** Consigne sans distance : « Tournez à gauche », « Au rond-point, prenez la 2e sortie »… */
export function instruction(m: Maneuver): string {
  let text: string;
  switch (m.kind) {
    case 'roundabout':
      text = m.exit
        ? t(`Au rond-point, prenez la ${ordinal(m.exit)} sortie`, `At the roundabout, take the ${ordinal(m.exit)} exit`)
        : t('Au rond-point, continuez sur le parcours', 'At the roundabout, stay on the route');
      break;
    case 'slight':
      text = t(`Serrez légèrement ${sideWord(m.side)}`, `Bear ${sideWord(m.side)}`);
      break;
    case 'sharp':
      text = t(`Virage serré ${sideWord(m.side)}`, `Turn sharp ${sideWord(m.side)}`);
      break;
    case 'uturn':
      text = t('Faites demi-tour', 'Make a U-turn');
      break;
    case 'keep':
      text =
        m.side === 'straight'
          ? t('Continuez tout droit', 'Continue straight ahead')
          : t(`Restez ${sideWord(m.side)}`, `Keep ${sideWord(m.side)}`);
      break;
    case 'arrive':
      return t('Vous êtes arrivé', 'You have arrived');
    default:
      text = t(`Tournez ${sideWord(m.side)}`, `Turn ${sideWord(m.side)}`);
  }
  return m.road ? `${text} ${t('sur', 'onto')} ${m.road}` : text;
}

/** Distance arrondie, prononçable : « 300 mètres », « 1,5 kilomètre ». */
export function spokenDistance(m: number): string {
  if (m < 1000) {
    const r = m < 100 ? Math.max(10, Math.round(m / 10) * 10) : Math.round(m / 50) * 50;
    return r >= 1000 ? t('1 kilomètre', '1 kilometre') : t(`${r} mètres`, `${r} metres`);
  }
  const km = Math.round(m / 100) / 10;
  const txt = Number.isInteger(km) ? String(km) : km.toFixed(1).replace('.', t(',', '.'));
  return t(`${txt} kilomètre${km >= 2 ? 's' : ''}`, `${txt} kilometre${km === 1 ? '' : 's'}`);
}

export function spokenInstruction(m: Maneuver, distance?: number): string {
  if (m.kind === 'arrive') {
    return distance
      ? t(`Arrivée dans ${spokenDistance(distance)}`, `Arriving in ${spokenDistance(distance)}`)
      : t('Vous êtes arrivé', 'You have arrived');
  }
  const base = instruction(m);
  return distance
    ? `${t('Dans', 'In')} ${spokenDistance(distance)}, ${base.charAt(0).toLowerCase()}${base.slice(1)}`
    : base;
}

/** Nom d'icône (MaterialCommunityIcons) pour la flèche. */
export function maneuverIcon(m: Maneuver): string {
  if (m.kind === 'arrive') return 'flag-checkered';
  if (m.kind === 'roundabout') return m.side === 'left' ? 'rotate-left' : 'rotate-right';
  if (m.kind === 'uturn') return m.side === 'right' ? 'arrow-u-right-top' : 'arrow-u-left-top';
  if (m.side === 'straight') return 'arrow-up-bold';
  if (m.kind === 'slight' || m.kind === 'keep') return m.side === 'left' ? 'arrow-top-left' : 'arrow-top-right';
  return m.side === 'left' ? 'arrow-left-top-bold' : 'arrow-right-top-bold';
}

// ---------------------------------------------------------------- détection géométrique (secours)

function classify(angle: number): { kind: ManeuverKind; side: Side } | null {
  const a = Math.abs(angle);
  const side: Side = angle < 0 ? 'left' : 'right';
  if (a < 35) return null;
  if (a < 60) return null; // simple courbe de la route : pas d'annonce
  if (a < 125) return { kind: 'turn', side };
  if (a < 165) return { kind: 'sharp', side };
  return { kind: 'uturn', side };
}

/**
 * Virages nets d'après la forme du tracé : changement de cap mesuré sur ±25 m.
 * Un virage de route très progressif n'est pas annoncé (pas assez « net »).
 */
export function detectTurns(route: LatLng[], cum = cumulativeDistances(route)): Maneuver[] {
  const total = cum[cum.length - 1] ?? 0;
  const ARM = 25,
    STEP = 5,
    MIN_GAP = 60;
  const candidates: { along: number; angle: number }[] = [];
  for (let s = ARM; s <= total - ARM; s += STEP) {
    const before = pointAtAlong(route, cum, s - ARM);
    const here = pointAtAlong(route, cum, s);
    const after = pointAtAlong(route, cum, s + ARM);
    candidates.push({ along: s, angle: turnAngle(bearing(before, here), bearing(here, after)) });
  }
  // garder le maximum local de chaque virage
  const out: Maneuver[] = [];
  let i = 0;
  while (i < candidates.length) {
    if (Math.abs(candidates[i].angle) < 35) {
      i++;
      continue;
    }
    let best = candidates[i];
    let j = i;
    while (
      j < candidates.length &&
      Math.abs(candidates[j].angle) >= 35 &&
      Math.sign(candidates[j].angle) === Math.sign(best.angle)
    ) {
      if (Math.abs(candidates[j].angle) > Math.abs(best.angle)) best = candidates[j];
      j++;
    }
    const c = classify(best.angle);
    const last = out[out.length - 1];
    if (c && (!last || best.along - last.along >= MIN_GAP)) {
      out.push({ along: best.along, ...c, location: pointAtAlong(route, cum, best.along) });
    }
    i = j;
  }
  return withArrival(out, route, cum);
}

/** Manœuvres utiles en roulant : on retire les « légèrement » (souvent une simple courbe). */
/** Importance d'une manœuvre quand plusieurs tombent au même endroit (on garde la plus importante). */
const PRIORITY: Record<ManeuverKind, number> = {
  arrive: 6,
  roundabout: 5,
  uturn: 4,
  sharp: 3,
  turn: 2,
  keep: 1,
  slight: 0,
};

/** Manœuvres à moins de N m l'une de l'autre : une seule annonce (la plus importante). */
export const SAME_SPOT_M = 30;
/** Après un rond-point : ses courbes et sa sortie ne sont pas des manœuvres de plus. */
export const AFTER_ROUNDABOUT_M = 80;

/**
 * Manœuvres à annoncer : sans les « légèrement », sans les doublons au même endroit, et sans les
 * virages qui ne sont que la forme d'un rond-point (sinon une annonce tous les 50 m en l'approchant).
 */
export function relevantManeuvers(list: Maneuver[]): Maneuver[] {
  const sorted = list.filter((m) => m.kind !== 'slight').sort((a, b) => a.along - b.along);
  const out: Maneuver[] = [];
  for (const m of sorted) {
    const last = out[out.length - 1];
    if (last && m.kind !== 'arrive') {
      if (last.kind === 'roundabout' && m.along - last.along < AFTER_ROUNDABOUT_M) continue;
      if (m.along - last.along < SAME_SPOT_M) {
        if (PRIORITY[m.kind] > PRIORITY[last.kind]) out[out.length - 1] = m;
        continue;
      }
    }
    out.push(m);
  }
  return out;
}

export function withArrival(list: Maneuver[], route: LatLng[], cum: number[]): Maneuver[] {
  const total = cum[cum.length - 1] ?? 0;
  if (route.length === 0 || list.some((m) => m.kind === 'arrive')) return list;
  return [...list, { along: total, kind: 'arrive', side: 'straight', location: route[route.length - 1] }];
}

// ---------------------------------------------------------------- étapes OSRM → manœuvres

export type OsrmStep = {
  name?: string;
  maneuver: { type: string; modifier?: string; exit?: number; location: [number, number] };
};

function fromModifier(modifier: string | undefined): { kind: ManeuverKind; side: Side } | null {
  switch (modifier) {
    case 'uturn':
      return { kind: 'uturn', side: 'left' };
    case 'sharp left':
      return { kind: 'sharp', side: 'left' };
    case 'sharp right':
      return { kind: 'sharp', side: 'right' };
    case 'left':
      return { kind: 'turn', side: 'left' };
    case 'right':
      return { kind: 'turn', side: 'right' };
    case 'slight left':
    case 'slight right':
      return null; // la route continue en légère courbe : pas d'annonce (sauf embranchement)
    default:
      return null; // tout droit : pas d'annonce
  }
}

/** Traduit une étape OSRM en manœuvre (ou null si rien à annoncer). */
export function maneuverFromStep(step: OsrmStep): Omit<Maneuver, 'along'> | null {
  const { type, modifier, exit, location } = step.maneuver;
  const loc = { latitude: location[1], longitude: location[0] };
  const road = step.name?.trim() || undefined;
  switch (type) {
    case 'roundabout':
    case 'rotary': {
      const side: Side = modifier?.includes('left') ? 'left' : 'right';
      return { kind: 'roundabout', side, exit, location: loc, road };
    }
    case 'fork': {
      const side: Side = modifier?.includes('left') ? 'left' : modifier?.includes('right') ? 'right' : 'straight';
      return { kind: 'keep', side, location: loc, road };
    }
    case 'turn':
    case 'end of road':
    case 'on ramp':
    case 'off ramp':
    case 'roundabout turn': {
      const k = fromModifier(modifier);
      return k ? { ...k, location: loc, road } : null;
    }
    case 'continue': {
      // la route principale tourne : on n'annonce que le demi-tour
      return modifier === 'uturn' ? { kind: 'uturn', side: 'left', location: loc, road } : null;
    }
    default:
      return null; // depart, arrive, new name, merge, exit roundabout, notification…
  }
}

/**
 * Place les étapes OSRM le long du tracé (dans l'ordre, sans revenir en arrière).
 * `fromAlong` : position du tracé où commence ce morceau.
 * `maxDistance` : écart maximal (m) entre une étape et le tracé.
 */
export function maneuversFromSteps(
  steps: OsrmStep[],
  route: LatLng[],
  cum: number[],
  fromAlong = 0,
  toAlong = Infinity,
  maxDistance = 60,
): Maneuver[] {
  const out: Maneuver[] = [];
  let cursor = fromAlong;
  for (const step of steps) {
    const m = maneuverFromStep(step);
    if (!m) continue;
    const upper = Number.isFinite(toAlong) ? toAlong + 200 : cursor + 5000;
    const snap = snapToRoute(m.location, route, cum, Math.max(0, cursor - 30), upper);
    if (!snap || snap.distance > maxDistance) continue; // l'étape n'est pas sur notre tracé
    cursor = snap.along;
    const last = out[out.length - 1];
    if (last && snap.along - last.along < 15) continue; // doublon
    out.push({ ...m, along: snap.along });
  }
  return out;
}
